/**
 * use-permissions.ts — WAYP-267
 *
 * Provides:
 *   - useMyPermissions()          — fetches + syncs resolved perms for current user
 *   - usePermission(mod, action)  — checks a single permission from the store
 *   - useUserPermissions(id)      — admin: fetch another user's permissions
 *   - useUpdateUserPermissions()  — admin: save custom permissions overrides
 *
 * Use <PermissionGate> for declarative conditional rendering in JSX.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth-store';
import { usePermissionsStore, type UserPermissions, type PermissionModule } from '../stores/permissions-store';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PermissionsResponse {
  userId: string;
  role: string;
  customPermissions?: UserPermissions;
  resolvedPermissions: UserPermissions;
}

/* ------------------------------------------------------------------ */
/*  Fetch current user's permissions and sync to store                 */
/* ------------------------------------------------------------------ */

export function useMyPermissions() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setPermissions = usePermissionsStore((s) => s.setPermissions);

  const query = useQuery({
    queryKey: ['my-permissions'],
    queryFn: async () => {
      const res = await api.get<{ data: PermissionsResponse }>('/users/me/permissions');
      return res.data.data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 min
  });

  // Sync resolved permissions into the Zustand store
  useEffect(() => {
    if (query.data?.resolvedPermissions) {
      setPermissions(query.data.resolvedPermissions);
    }
  }, [query.data, setPermissions]);

  return query;
}

/* ------------------------------------------------------------------ */
/*  Admin: fetch a specific user's permissions                         */
/* ------------------------------------------------------------------ */

export function useUserPermissions(userId: string | null) {
  return useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: async () => {
      const res = await api.get<{ data: PermissionsResponse }>(`/users/${userId}/permissions`);
      return res.data.data;
    },
    enabled: !!userId,
  });
}

/* ------------------------------------------------------------------ */
/*  WAYP-267: Convenience hook — check a single permission            */
/* ------------------------------------------------------------------ */

/**
 * usePermission(module, action)
 *
 * Returns true if the current user has the given permission.
 * Before permissions are loaded this returns true (optimistic) to
 * avoid flashing hidden content — the server still enforces at the API.
 *
 * Usage:
 *   const canCreateLoad = usePermission('loads', 'create');
 *   if (!canCreateLoad) return null;
 */
export function usePermission(module: PermissionModule, action: string): boolean {
  return usePermissionsStore((s) => s.can(module, action));
}

/**
 * useCanView(module)
 *
 * Shorthand for usePermission(module, 'view').
 */
export function useCanView(module: PermissionModule): boolean {
  return usePermissionsStore((s) => s.canView(module));
}

/* ------------------------------------------------------------------ */
/*  Admin: save a user's custom permissions                            */
/* ------------------------------------------------------------------ */

export function useUpdateUserPermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      permissions,
    }: {
      userId: string;
      permissions: UserPermissions;
    }) => {
      const res = await api.put<{ data: PermissionsResponse }>(
        `/users/${userId}/permissions`,
        { permissions },
      );
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-permissions', variables.userId] });
    },
  });
}
